import { fetchPricesForPriceGroups, getPriceGroupMoqTiers, getPriceGroupPrice } from './demPoPricing';
import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';
import type { DemItem } from '../types/dem';
import { singaporeTodayDateString } from '../utils/dateTime';

const ITEM_SELECT =
  'code,description,uom,packing,image_url,is_blocked,inventory,stock_status,chinese_description,status';

export type CataloguePriceTier = 'CATERING' | 'CATERING2';

export const CATALOGUE_PRICE_GROUPS: Record<CataloguePriceTier, string> = {
  CATERING: 'CATERING',
  CATERING2: 'CATERING2',
};

export type CataloguePriceMoqTier = {
  minQty: number;
  price: number;
};

export type CatalogueItem = DemItem & {
  inventory_posting_group: string;
  base_uom: string;
  unitPriceCatering: number;
  unitPriceCatering2: number;
  moqTiersCatering: CataloguePriceMoqTier[];
  moqTiersCatering2: CataloguePriceMoqTier[];
  outOfStock: boolean;
  enrichPending?: boolean;
  pricesPending?: boolean;
};

type PowerBiEnrichment = {
  ipg: string;
  inventory: number;
  chinese_description: string;
  packing: string;
  base_uom: string;
  sales_uom: string;
};

function rowToItem(row: Record<string, unknown>): DemItem {
  const inventoryRaw = row.inventory;
  let inventory = 0;
  if (typeof inventoryRaw === 'number') inventory = inventoryRaw;
  else if (inventoryRaw != null) {
    const parsed = Number(inventoryRaw);
    inventory = Number.isFinite(parsed) ? parsed : 0;
  }

  return {
    code: String(row.code ?? ''),
    description: String(row.description ?? ''),
    uom: String(row.uom ?? ''),
    packing: String(row.packing ?? ''),
    image_url: String(row.image_url ?? ''),
    is_blocked: Boolean(row.is_blocked),
    inventory,
    stock_status: String(row.stock_status ?? ''),
    chinese_description: String(row.chinese_description ?? ''),
    status: String(row.status ?? ''),
  };
}

function isOutOfStock(stockStatus: string, inventory: number): boolean {
  return stockStatus.trim().toLowerCase() === 'out of stock' || inventory <= 0;
}

async function fetchAllActiveDemItems(): Promise<DemItem[]> {
  const db = getDbSupabase('dem');
  const pageSize = 1000;
  const all: DemItem[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await db
      .from('items')
      .select(ITEM_SELECT)
      .eq('status', 'Active')
      .not('code', 'ilike', 'AP-%')
      .order('code', { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) throw new Error(formatSupabaseError(error));

    const rows = (data ?? []).map((row) => rowToItem(row as unknown as Record<string, unknown>));
    all.push(...rows);
    if (rows.length < pageSize) break;
    from += pageSize;
  }

  return all;
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  try {
    const { data } = await getDbSupabase('crm').auth.getSession();
    const token = data.session?.access_token?.trim();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // proxy rejects unauthenticated
  }
  return headers;
}

async function fetchPowerBiEnrichmentMap(): Promise<Map<string, PowerBiEnrichment>> {
  const headers = await authHeaders();
  const response = await fetch('/catalogue-api', { headers });
  const json = (await response.json().catch(() => null)) as
    | {
        error?: string;
        detail?: string;
        hint?: string;
        items?: Array<Record<string, unknown>>;
      }
    | null;

  if (!response.ok) {
    const detail =
      typeof json?.detail === 'string' && json.detail.trim() ? ` (${json.detail.trim()})` : '';
    const hint =
      typeof json?.hint === 'string' && json.hint.trim() ? ` ${json.hint.trim()}` : '';
    throw new Error((json?.error || `Failed to load PowerBI enrichment (${response.status}).`) + detail + hint);
  }

  const map = new Map<string, PowerBiEnrichment>();
  for (const raw of json?.items ?? []) {
    const no = String(raw.no ?? '')
      .trim()
      .toUpperCase();
    if (!no) continue;
    map.set(no, {
      ipg: String(raw.ipg ?? '').trim(),
      inventory: Number(raw.inventory ?? 0) || 0,
      chinese_description: String(raw.chinese_description ?? '').trim(),
      packing: String(raw.packing ?? '').trim(),
      base_uom: String(raw.base_uom ?? '').trim(),
      sales_uom: String(raw.sales_uom ?? raw.uom ?? '').trim(),
    });
  }
  return map;
}

function resolveGroupPrices(
  item: DemItem,
  groupPrices: Awaited<ReturnType<typeof fetchPricesForPriceGroups>>,
  deliveryDate: string,
): Pick<
  CatalogueItem,
  'unitPriceCatering' | 'unitPriceCatering2' | 'moqTiersCatering' | 'moqTiersCatering2'
> {
  const base = {
    itemCode: item.code,
    uom: item.uom,
    deliveryDate,
  };
  const moqTiersCatering = getPriceGroupMoqTiers(groupPrices, {
    ...base,
    priceGroup: CATALOGUE_PRICE_GROUPS.CATERING,
  });
  const moqTiersCatering2 = getPriceGroupMoqTiers(groupPrices, {
    ...base,
    priceGroup: CATALOGUE_PRICE_GROUPS.CATERING2,
  });
  return {
    moqTiersCatering,
    moqTiersCatering2,
    unitPriceCatering: getPriceGroupPrice(groupPrices, {
      ...base,
      qty: 1,
      priceGroup: CATALOGUE_PRICE_GROUPS.CATERING,
    }),
    unitPriceCatering2: getPriceGroupPrice(groupPrices, {
      ...base,
      qty: 1,
      priceGroup: CATALOGUE_PRICE_GROUPS.CATERING2,
    }),
  };
}

function toCatalogueItem(
  item: DemItem,
  options?: {
    enrich?: PowerBiEnrichment;
    groupPrices?: Awaited<ReturnType<typeof fetchPricesForPriceGroups>>;
    deliveryDate?: string;
    enrichPending?: boolean;
    pricesPending?: boolean;
  },
): CatalogueItem {
  const enrich = options?.enrich;
  const inventory = enrich ? enrich.inventory : item.inventory;
  const stockStatus = enrich
    ? inventory <= 0
      ? 'Out of Stock'
      : 'In Stock'
    : item.stock_status;
  const deliveryDate = options?.deliveryDate ?? singaporeTodayDateString();
  const groupPrices = options?.groupPrices
    ? resolveGroupPrices(item, options.groupPrices, deliveryDate)
    : {
        unitPriceCatering: 0,
        unitPriceCatering2: 0,
        moqTiersCatering: [],
        moqTiersCatering2: [],
      };

  return {
    ...item,
    chinese_description: item.chinese_description || enrich?.chinese_description || '',
    packing: item.packing || enrich?.packing || '',
    uom: enrich?.sales_uom || item.uom || enrich?.base_uom || '',
    base_uom: enrich?.base_uom || item.uom || enrich?.sales_uom || '',
    inventory,
    stock_status: stockStatus,
    inventory_posting_group: enrich?.ipg ?? '',
    ...groupPrices,
    outOfStock: isOutOfStock(stockStatus, inventory),
    enrichPending: options?.enrichPending ?? false,
    pricesPending: options?.pricesPending ?? false,
  };
}

export function catalogueDisplayPrice(item: CatalogueItem, tier: CataloguePriceTier): number {
  if (tier === 'CATERING2') {
    if (item.unitPriceCatering2 > 0) return item.unitPriceCatering2;
    // Nav CATERING2 rows missing — convention is +10% over CATERING.
    if (item.unitPriceCatering > 0) return item.unitPriceCatering * 1.1;
    return 0;
  }
  return item.unitPriceCatering;
}

export function cataloguePriceTierLabel(tier: CataloguePriceTier): string {
  return tier === 'CATERING2' ? 'CATERING2 (+10%)' : 'CATERING';
}

/** True when CATERING2 is derived from CATERING × 1.1 (no Nav row). */
export function cataloguePriceIsDerived(item: CatalogueItem, tier: CataloguePriceTier): boolean {
  if (tier !== 'CATERING2') return false;
  if (item.moqTiersCatering2.length > 0) return false;
  return item.moqTiersCatering.length > 0 || item.unitPriceCatering > 0;
}

export function catalogueDisplayMoqTiers(
  item: CatalogueItem,
  tier: CataloguePriceTier,
): CataloguePriceMoqTier[] {
  const base = tier === 'CATERING2' ? item.moqTiersCatering2 : item.moqTiersCatering;
  if (tier === 'CATERING2' && base.length === 0 && item.moqTiersCatering.length > 0) {
    return item.moqTiersCatering.map((row) => ({
      minQty: row.minQty,
      price: row.price * 1.1,
    }));
  }
  return base;
}

export function formatCatalogueMoqLabel(minQty: number): string {
  if (!Number.isFinite(minQty) || minQty <= 1) return 'MOQ 1+';
  return `MOQ ${Math.round(minQty).toLocaleString()}+`;
}

/** IPGs that stay visible in catalogue even when out of stock. */
export const CATALOGUE_OOS_VISIBLE_IPGS = new Set(['FG-XOC', 'FG-T', 'FG-R']);

export function catalogueIpgShowsWhenOutOfStock(ipg: string): boolean {
  return CATALOGUE_OOS_VISIBLE_IPGS.has(ipg.trim().toUpperCase());
}

/** Hide out-of-stock items unless IPG is FG-XOC, FG-T, or FG-R. */
export function shouldShowCatalogueItem(item: CatalogueItem): boolean {
  if (!item.outOfStock) return true;
  if (item.enrichPending) return true;
  return catalogueIpgShowsWhenOutOfStock(item.inventory_posting_group);
}

/** Price rows for display — always at least one row when a unit price exists. */
export function catalogueDisplayPriceRows(
  item: CatalogueItem,
  tier: CataloguePriceTier,
): CataloguePriceMoqTier[] {
  const moqTiers = catalogueDisplayMoqTiers(item, tier);
  if (moqTiers.length > 0) return moqTiers;
  const price = catalogueDisplayPrice(item, tier);
  if (price > 0) return [{ minQty: 0, price }];
  return [];
}

export function cataloguePriceRowShowsMoq(
  tier: CataloguePriceMoqTier,
  tiers: CataloguePriceMoqTier[],
): boolean {
  if (tiers.length > 1) return true;
  return tier.minQty > 1;
}

/** B2B-style multi-keyword search (code + description + chinese + IPG). */
export function matchesCatalogueSearch(
  item: Pick<CatalogueItem, 'code' | 'description' | 'chinese_description' | 'inventory_posting_group'>,
  query: string,
): boolean {
  const keywords = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (keywords.length === 0) return true;
  const pool =
    `${item.code} ${item.description} ${item.chinese_description} ${item.inventory_posting_group}`.toLowerCase();
  return keywords.every((kw) => pool.includes(kw));
}

/**
 * DEM items first, then PowerBI IPG/inventory + CATERING / CATERING2 group prices.
 */
export async function loadCatalogueItems(options?: {
  onPartial?: (items: CatalogueItem[]) => void;
}): Promise<{ items: CatalogueItem[]; enrichError: string | null; pricesError: string | null }> {
  const deliveryDate = singaporeTodayDateString();
  const demItems = await fetchAllActiveDemItems();
  const partial = demItems.map((item) =>
    toCatalogueItem(item, { enrichPending: true, pricesPending: true }),
  );
  options?.onPartial?.(partial);

  let enrichMap = new Map<string, PowerBiEnrichment>();
  let enrichError: string | null = null;
  let groupPrices: Awaited<ReturnType<typeof fetchPricesForPriceGroups>> = [];
  let pricesError: string | null = null;

  const [enrichResult, pricesResult] = await Promise.allSettled([
    fetchPowerBiEnrichmentMap(),
    fetchPricesForPriceGroups([CATALOGUE_PRICE_GROUPS.CATERING, CATALOGUE_PRICE_GROUPS.CATERING2]),
  ]);

  if (enrichResult.status === 'fulfilled') {
    enrichMap = enrichResult.value;
  } else {
    enrichError =
      enrichResult.reason instanceof Error
        ? enrichResult.reason.message
        : 'Failed to load PowerBI IPG / inventory.';
  }

  if (pricesResult.status === 'fulfilled') {
    groupPrices = pricesResult.value;
  } else {
    pricesError =
      pricesResult.reason instanceof Error
        ? pricesResult.reason.message
        : 'Failed to load CATERING price group prices.';
  }

  const items = demItems.map((item) =>
    toCatalogueItem(item, {
      enrich: enrichMap.get(item.code.trim().toUpperCase()),
      groupPrices,
      deliveryDate,
      enrichPending: false,
      pricesPending: false,
    }),
  );

  return { items, enrichError, pricesError };
}

export function formatCatalogueUnitPrice(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  return `S$${value.toFixed(4)}`;
}

export function formatCataloguePriceWithUom(value: number, uom: string): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  const unit = uom.trim() || 'unit';
  return `S$${value.toFixed(4)} per ${unit}`;
}

export function formatCatalogueInventory(
  value: number,
  outOfStock: boolean,
  baseUom?: string,
): string {
  if (outOfStock) return 'Out of stock';
  if (!Number.isFinite(value)) return '—';
  const qty = Math.round(value).toLocaleString();
  const unit = baseUom?.trim();
  return unit ? `${qty} ${unit}` : qty;
}

export const CATALOGUE_PAGE_SIZE = 48;
